import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CopyHistoryItem } from "../types/lead-contacts.types";

interface CopyHistoryModalProps {
  open: boolean;
  onClose: () => void;
  history: CopyHistoryItem[];
  loading: boolean;
  page: number;
  total: number;
  hasMore: boolean;
  onFilterChange?: (
    filters: {
      leadId?: number;
      coordId?: number;
      campaignName?: string;
      origin?: "normal" | "repescagem";
    },
    page?: number,
  ) => void;
  onPageChange?: (page: number) => void;
}

const CopyHistoryModal: React.FC<CopyHistoryModalProps> = ({
  open,
  onClose,
  history,
  loading,
  page,
  total,
  hasMore,
  onFilterChange,
}) => {
  const [filters, setFilters] = useState<{
    leadId?: number;
    coordId?: number;
    campaignName?: string;
    origin?: "normal" | "repescagem";
  }>({});

  const [showFilters, setShowFilters] = useState(false);
  const hasLoadedInitialData = useRef(false);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  // Extrair listas únicas de coordenadores e campanhas do histórico
  const coordinators = useMemo(() => {
    const coordsMap = new Map<number, string>();
    history.forEach((item) => {
      if (item.coord_id && item.coord_name) {
        coordsMap.set(item.coord_id, item.coord_name);
      }
    });
    return Array.from(coordsMap.entries()).map(([id, name]) => ({ id, name }));
  }, [history]);

  const campaigns = useMemo(() => {
    const campaignsSet = new Set<string>();
    history.forEach((item) => {
      if (item.campaign_name) {
        campaignsSet.add(item.campaign_name);
      }
    });
    return Array.from(campaignsSet).sort();
  }, [history]);

  const formatHistoryDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value === "" || value === null ? undefined : value,
    };
    setFilters(newFilters);
    if (onFilterChange) {
      // Resetar para página 1 quando filtros mudarem
      onFilterChange(newFilters, 1);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    if (onFilterChange) {
      onFilterChange(clearedFilters, 1);
    }
  };

  const handlePageChange = (newPage: number) => {
    // Validar página antes de mudar
    const totalPages = Math.ceil(total / 20);
    const validPage = Math.max(1, Math.min(newPage, totalPages || 1));

    // Manter os filtros atuais ao mudar de página
    if (onFilterChange) {
      onFilterChange(filters, validPage);
    }
  };

  const totalPages = Math.ceil(total / 20);

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.leadId ||
      filters.coordId ||
      filters.campaignName ||
      filters.origin
    );
  }, [filters]);

  useEffect(() => {
    const fn = onFilterChangeRef.current;
    if (open && fn && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      fn({}, 1);
    } else if (!open) {
      setFilters({});
      setShowFilters(false);
      hasLoadedInitialData.current = false;
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            maxHeight: "80vh",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 24px 16px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon sx={{ color: "#008069" }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Histórico de Cópias
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#666",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px 24px" }}>
        {/* Filtros */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Button
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                textTransform: "none",
                color: "#666",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "rgba(0, 128, 105, 0.08)",
                },
              }}
            >
              {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
              {hasActiveFilters && (
                <Box
                  sx={{
                    ml: 1,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#008069",
                  }}
                />
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                onClick={handleClearFilters}
                size="small"
                sx={{
                  textTransform: "none",
                  color: "#666",
                  fontSize: "12px",
                }}
              >
                Limpar Filtros
              </Button>
            )}
          </Box>

          {showFilters && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
              }}
            >
              <TextField
                label="ID do Lead"
                type="number"
                value={filters.leadId || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "leadId",
                    e.target.value ? parseInt(e.target.value) : undefined,
                  )
                }
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Coordenador</InputLabel>
                <Select
                  value={filters.coordId || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "coordId",
                      e.target.value
                        ? parseInt(e.target.value as unknown as string)
                        : undefined,
                    )
                  }
                  label="Coordenador"
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {coordinators.map((coord) => (
                    <MenuItem key={coord.id} value={coord.id}>
                      {coord.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Campanha</InputLabel>
                <Select
                  value={filters.campaignName || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "campaignName",
                      e.target.value || undefined,
                    )
                  }
                  label="Campanha"
                >
                  <MenuItem value="">
                    <em>Todas</em>
                  </MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign} value={campaign}>
                      {campaign}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Origem</InputLabel>
                <Select
                  value={filters.origin || ""}
                  onChange={(e) =>
                    handleFilterChange("origin", e.target.value || undefined)
                  }
                  label="Origem"
                >
                  <MenuItem value="">
                    <em>Todas</em>
                  </MenuItem>
                  <MenuItem value="normal">Campanha Normal</MenuItem>
                  <MenuItem value="repescagem">Repescagem</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
          >
            <CircularProgress size={32} sx={{ color: "#008069" }} />
          </Box>
        ) : history.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#666",
            }}
          >
            <HistoryIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {hasActiveFilters
                ? "Nenhuma cópia encontrada com os filtros aplicados"
                : "Nenhuma cópia registrada ainda"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              {hasActiveFilters
                ? "Tente ajustar os filtros"
                : "As cópias de leads aparecerão aqui"}
            </Typography>
          </Box>
        ) : (
          <List sx={{ padding: 0 }}>
            {history.map((item: CopyHistoryItem, index: number) => (
              <ListItem
                key={index}
                sx={{
                  padding: "12px 0",
                  borderBottom:
                    index < history.length - 1 ? "1px solid #f0f0f0" : "none",
                  "&:hover": {
                    backgroundColor: "rgba(0, 128, 105, 0.02)",
                  },
                }}
              >
                <ListItemText
                  primaryTypographyProps={{ component: "div" }}
                  secondaryTypographyProps={{ component: "div" }}
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontWeight: 600, color: "#333" }}
                        >
                          #{item.lead_id}
                        </Typography>
                        {item.campaign_name && (
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: "#666", fontSize: "11px" }}
                          >
                            • {item.campaign_name}
                          </Typography>
                        )}
                        {item.origin && (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "1px 6px",
                              borderRadius: "8px",
                              fontSize: "9px",
                              fontWeight: 500,
                              backgroundColor:
                                item.origin === "repescagem"
                                  ? "#fff3e0"
                                  : "#e8f5e9",
                              color:
                                item.origin === "repescagem"
                                  ? "#e65100"
                                  : "#2e7d32",
                            }}
                          >
                            {item.origin === "repescagem"
                              ? "Repescagem"
                              : "Normal"}
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: "#999", fontSize: "11px" }}
                      >
                        {formatHistoryDate(item.copied_at)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mt: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      {item.user_name && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{ color: "#666", fontSize: "11px" }}
                        >
                          Por: {item.user_name}
                        </Typography>
                      )}
                      {item.coord_name && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{ color: "#666", fontSize: "11px" }}
                        >
                          • Coord: {item.coord_name}
                        </Typography>
                      )}
                      {item.lead_name && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{ color: "#666", fontSize: "11px" }}
                        >
                          • {item.lead_name}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px 20px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: "#666", fontSize: "12px" }}
          >
            {total > 0
              ? `Mostrando ${(page - 1) * 20 + 1}-${Math.min(page * 20, total)} de ${total}`
              : "Nenhum resultado"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1 || loading}
            size="small"
            sx={{
              color: page <= 1 ? "#ccc" : "#666",
              "&:hover": {
                backgroundColor:
                  page <= 1 ? "transparent" : "rgba(0, 128, 105, 0.08)",
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              minWidth: "60px",
              textAlign: "center",
              fontSize: "13px",
            }}
          >
            {totalPages > 0 ? `${page} / ${totalPages}` : "0 / 0"}
          </Typography>
          <IconButton
            onClick={() => handlePageChange(page + 1)}
            disabled={!hasMore || loading}
            size="small"
            sx={{
              color: !hasMore ? "#ccc" : "#666",
              "&:hover": {
                backgroundColor: !hasMore
                  ? "transparent"
                  : "rgba(0, 128, 105, 0.08)",
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              ml: 2,
              color: "#666",
              borderColor: "#ccc",
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#999",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Fechar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CopyHistoryModal;
